import { api, CustomAxiosRequestConfig } from "@/shared/api/client";
import { queryClient } from "@/shared/lib/queryClient";
import { queryKeys } from "@/shared/lib/queryKeys";
import {
    SystemProfileGetDTO,
    SystemProfilesGetDTO,
    SystemProfileCreateDTO,
    SystemProfileUpdateDTO,
    SystemProfileQueryFilter,
    PageSystemProfilesGetDTO,
} from "@/shared/types/admin/systemProfile";

/** Fornecedores sao perfis de sistema: qualquer mutacao pode alterar as listas e buscas em cache. */
const invalidateSuppliers = () => queryClient.invalidateQueries({ queryKey: queryKeys.suppliers.all });

export const systemProfileService = {
    getAll: async (params: {
        page: number;
        size: number;
        sort?: string[];
        filter?: SystemProfileQueryFilter;
    }): Promise<PageSystemProfilesGetDTO> => {
        const response = await api.get<PageSystemProfilesGetDTO>("/system-profiles", {
            params: {
                page: params.page - 1,
                size: params.size,
                sort: params.sort,
                firstName: params.filter?.firstName,
                phoneNumber: params.filter?.phoneNumber,
                email: params.filter?.email,
                active: params.filter?.active,
                type: params.filter?.type?.toUpperCase(),
            },
        });
        return response.data;
    },

    getById: async (id: string): Promise<SystemProfileGetDTO> => {
        const response = await api.get<SystemProfileGetDTO>(`/system-profiles/${id}`, {
            skipErrorToast: true
        });
        return response.data;
    },

    create: async (data: SystemProfileCreateDTO): Promise<SystemProfileGetDTO> => {
        const payload = { ...data, type: data.type.toUpperCase() as never };
        const response = await api.post<SystemProfileGetDTO>("/system-profiles", payload);
        void invalidateSuppliers();
        return response.data;
    },

    update: async (id: string, data: SystemProfileUpdateDTO): Promise<SystemProfileGetDTO> => {
        const payload = { ...data };
        if (payload.type) {
            payload.type = payload.type.toUpperCase() as never;
        }
        const response = await api.put<SystemProfileGetDTO>(`/system-profiles/${id}`, payload);
        void invalidateSuppliers();
        return response.data;
    },

    disable: async (id: string): Promise<void> => {
        await api.patch(`/system-profiles/${id}`);
        void invalidateSuppliers();
    },

    delete: async (id: string): Promise<void> => {
        await api.delete(`/system-profiles/${id}`);
        void invalidateSuppliers();
    },

    getProfileImage: async (id: string): Promise<Blob> => {
        const response = await api.get(`/system-profiles/${id}/profile/img`, {
            responseType: "blob",
            skipErrorToast: true
        });
        return response.data;
    },

    uploadProfileImage: async (id: string, file: File): Promise<unknown> => {
        const formData = new FormData();
        formData.append("file", file);
        const { data } = await api.put(`/system-profiles/${id}/profile/img`, formData);
        return data;
    },
};
